import type {TrackDispatcher} from '@gaia/hooks/useTracks';
import type {SharedData} from '@gaia/types';
import fetchJSON from '@gaia/utils/fetchJSON';
import {registerSavedWaypoint} from '@gaia/utils/gaia/saveWaypoint';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';

// eslint-disable-next-line max-params
export default function (
    action: TakeLocalWaypointAction,
    waypoints: {[localId: string]: Waypoint},
    setWaypoints: React.Dispatch<React.SetStateAction<{[localId: string]: Waypoint}>>,
    trackPoints: {[localId: string]: Waypoint},
    trackDispatcher: TrackDispatcher,
    sharedData: SharedData
) {
    const {localId, id} = action.waypoint.properties;

    if (localId && trackPoints[localId]) {
        if (action.callback) action.callback(trackPoints[localId]);
        return [localId];
    }

    // A trackPoint that has already been taken local
    if (id && trackPoints[id]) {
        if (action.callback) action.callback(trackPoints[id]);
        return [id];
    }

    // A trackPoint that is not yet local
    if (id && action.waypoint.properties.track_id) {
        trackDispatcher({
            type: 'takeLocal',
            track: {
                properties: {
                    id: action.waypoint.properties.track_id,
                    isPublicTrack: false,
                },
            },
        });
        return [id];

        // A waypoint that is local
    } else if (id && waypoints[id]) {
        if (action.callback) action.callback(waypoints[id]);
        return [id];

        // A waypoint that is local
    } else if (localId && waypoints[localId]) {
        if (action.callback) action.callback(waypoints[localId]);
        return [localId];

        // A waypoint that is not yet local
    } else {
        if (!id) throw new Error('Cannot call takeLocal on a waypoint without an id');

        const waypoint1: OnePartial<Waypoint, 'geometry'> = {
            type: 'Feature',
            properties: {
                ...(mapValues(omit(action.waypoint.properties, 'photos'), (value: any) => {
                    if (value === 'null') return null;
                    else return value;
                }) as RemoteWaypoint['properties']),
                id,
                localId: id,
                archived: false,
                isLocallyCreated: false,
                type: 'waypoint',
            },
            geometry: action.waypoint.geometry,
        };

        if (waypoint1.geometry) {
            registerSavedWaypoint(waypoint1 as Waypoint);
            setWaypoints((latestWaypoints) => ({
                ...latestWaypoints,
                [id]: waypoint1 as Waypoint,
            }));
        }

        const pubLink: string =
            sharedData &&
            (sharedData.type === 'waypoint' || sharedData.type === 'folder') &&
            sharedData.pubLink
                ? `?pubLink=${sharedData.pubLink}`
                : '';

        fetchJSON(`/api/objects/waypoint/${waypoint1.properties.id}/${pubLink}`, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
        })
            .then((waypoint2: RemoteWaypoint) => {
                setWaypoints((latestWaypoints) => {
                    const latestWaypoint: Waypoint = {
                        type: 'Feature',
                        geometry: waypoint2.geometry,
                        properties: {
                            ...waypoint1.properties,
                            ...waypoint2.properties,
                            ...latestWaypoints[id]?.properties,
                        },
                    };

                    registerSavedWaypoint(latestWaypoint);
                    if (action.callback) action.callback(latestWaypoint);

                    return {
                        ...latestWaypoints,
                        [id]: latestWaypoint,
                    };
                });
            })
            .catch(console.warn);

        return [id];
    }
}
